import { goldBarsService } from "@shared/src/services/goldbars.service";
import { ixToast } from "@shared/src/utils/ixToast";

const REFERRER_KEY = "ix_referrer";
const REFERRER_EXPIRY_KEY = "ix_referrer_expiry";
const REFERRER_EXPIRY_DAYS = 30; // Store referrer for 30 days

export const extractReferrer = (): string | null => {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const referrer = urlParams.get("referrer");

  return referrer;
};

export const storeReferrer = (referrer: string): void => {
  if (typeof window === "undefined") return;

  // First referrer wins - only store if there isn't already a valid referrer
  const existingReferrer = getReferrer();

  // If there's already a valid referrer, don't overwrite it
  if (existingReferrer) {
    // Still remove the referrer from URL even if we don't store it
    removeReferrerFromUrl();
    return;
  }

  // Store the referrer since there isn't an existing one
  localStorage.setItem(REFERRER_KEY, referrer);

  // Set expiry date
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + REFERRER_EXPIRY_DAYS);
  localStorage.setItem(REFERRER_EXPIRY_KEY, expiryDate.toISOString());

  // Remove the referrer parameter from the URL
  removeReferrerFromUrl();
};

// Helper function to remove referrer from URL
const removeReferrerFromUrl = (): void => {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  url.searchParams.delete("referrer");

  // Replace the current URL without reloading the page
  window.history.replaceState({}, document.title, url.toString());
};

export const getReferrer = (): string | null => {
  if (typeof window === "undefined") return null;

  const referrer = localStorage.getItem(REFERRER_KEY);
  const expiryStr = localStorage.getItem(REFERRER_EXPIRY_KEY);

  // Check if referrer has expired
  if (referrer && expiryStr) {
    const expiry = new Date(expiryStr);
    if (expiry > new Date()) {
      return referrer;
    } else {
      // Clear expired referrer
      clearReferrer();
    }
  }

  return null;
};

export const clearReferrer = (): void => {
  if (typeof window === "undefined") return;

  localStorage.removeItem(REFERRER_KEY);
  localStorage.removeItem(REFERRER_EXPIRY_KEY);
};

export const processReferralAfterSignup = async (): Promise<void> => {
  const referrer = getReferrer();

  if (referrer) {
    try {
      await goldBarsService.referUser(referrer);
      // Clear referrer after processing
      clearReferrer();
    } catch (error: any) {
      // Show error toast but don't interrupt the flow
      ixToast.error(error.message || "Failed to process referral");
      // Clear referrer even on error - we won't retry
      clearReferrer();
    }
  }
};
